// ---------------------------------------------------------------------------
// Funding & Partnerships page — editable content.
//
// All copy for /funding lives here so it can be reviewed and edited without
// touching page structure. Nothing here may imply that Bioregional Ventures
// operates a fund, has capital available, awards grants or offers investment
// products.
// ---------------------------------------------------------------------------

export const fundingHero = {
  eyebrow: "Funding & Partnerships",
  headline: "Financing Regeneration",
  body: [
    "Regeneration requires more than short-term grants. It requires financing approaches capable of supporting land stewardship, productive enterprises, shared infrastructure and long-term community benefit.",
    "Bioregional Ventures combines practical project development with funding strategy and is exploring opportunities for catalytic investment and philanthropic partnerships. We help organisations assess what their projects need, prepare credible proposals and consider financing approaches appropriate to their purpose and capacity.",
  ],
  primaryCtas: [
    { label: "Find a pathway for your project", target: "project-pathway" },
    { label: "Explore ways to contribute", target: "funder-pathway" },
  ],
  links: [
    { label: "Funding Research & Grants", href: "/funding#funding-pathways" },
    { label: "Project & Investment Readiness", href: "/funding/projects" },
    { label: "Beyond Grants: Designing Finance for Regeneration", href: "/funding/beyond-grants" },
    { label: "Bioregional Capital Partners", href: "/capital-partners" },
  ],
};

export const fundingNeeds = {
  heading: "What needs funding?",
  note: "No particular funding source is automatically available for these activities — suitability is assessed project by project.",
  closing:
    "Strong projects depend on more than physical assets. Community participation, coordination, shared learning and ongoing stewardship also need resources. We help identify these costs early and include them in a realistic funding and operating plan.",
  cards: [
    {
      title: "Early groundwork",
      text: "Community engagement, local resource mapping, feasibility, specialist advice, governance design, financial modelling and practical trials through Bioregional Design Studios.",
    },
    {
      title: "Land and secure access",
      text: "Exploring acquisition, long-term tenure and arrangements that protect the project's purpose.",
    },
    {
      title: "Shared infrastructure",
      text: "Water, storage, processing, equipment, access and facilities used by multiple enterprises.",
    },
    {
      title: "Enterprise development",
      text: "Trials, establishment costs, skills, working capital and routes to market.",
    },
    {
      title: "Restoration and stewardship",
      text: "Ecological assessment, rehabilitation, monitoring and ongoing care of soil, water and habitat.",
    },
  ],
};

export const fundingPathways = [
  {
    id: "philanthropy",
    title: "Philanthropy",
    standfirst: "Support the foundations and shared benefits.",
    body: "Philanthropic support may help resource community participation, feasibility, restoration, learning and other work that delivers public benefit without a dependable commercial income stream.",
    contributorsLabel: "Potential contributors",
    contributors:
      "Individuals, charitable foundations, donor-advised giving arrangements and philanthropic intermediaries.",
    questions: [
      "What public benefit will the support enable?",
      "Who can receive and account for the funding?",
      "Are any charitable or tax requirements relevant?",
      "What reporting is proportionate to the contribution?",
    ],
    note: "Tax deductibility is never promised. A donor-advised fund is a giving arrangement, not necessarily an open grant programme.",
    button: "Discuss philanthropic support",
    topic: "Philanthropic support",
  },
  {
    id: "grants",
    title: "Grants",
    standfirst: "Match a defined project with an eligible programme.",
    body: "Government and philanthropic grant programmes may support particular activities, locations or outcomes. A strong application starts with a sound project and a close fit with the programme's requirements.",
    contributorsLabel: null,
    contributors: null,
    questions: [
      "Is the applicant eligible?",
      "Are the activities and costs eligible?",
      "Are matched contributions required?",
      "Can the project meet delivery and reporting obligations?",
      "How will the work continue after the grant ends?",
    ],
    note: "",
    button: "Discuss grant readiness",
    topic: "Grant readiness",
  },
  {
    id: "investment",
    title: "Patient investment and lending",
    standfirst: "Connect repayable capital with realistic income.",
    body: "Some project components may be suitable for investment or lending where income, repayment capacity, risks and investor expectations can be clearly assessed.",
    contributorsLabel: null,
    contributors: null,
    questions: [
      "What income supports repayment or returns?",
      "What happens if revenue is delayed or lower than expected?",
      "What security or control would a funder require?",
      "How would an investor exit?",
      "How would community purpose and tenure be protected?",
    ],
    note: "This is a readiness conversation only — no investment commitments, transaction facilities, projected returns or offers to subscribe are made on this site.",
    button: "Discuss project finance readiness",
    topic: "Project finance readiness",
  },
];

export const fundingTogether = {
  heading: "One project can have several funding needs.",
  body: [
    "A community farm might need support for feasibility, shared infrastructure, enterprise establishment and restoration. Those activities do not all generate income in the same way.",
    "A practical funding plan identifies each cost, who benefits, the appropriate recipient and whether repayment is realistic. Each contribution needs clear terms, responsibilities and accounting.",
  ],
  table: {
    columns: ["Project component", "Funding sources to investigate"],
    rows: [
      ["Community engagement and feasibility", "Philanthropy or suitable grants"],
      ["Land access or acquisition", "Donations, grants, contributions or appropriately structured finance"],
      ["Shared infrastructure", "Grants, contributions and finance where service income supports it"],
      ["Trading enterprise", "Operator capital and suitable repayable finance"],
      ["Restoration and monitoring", "Philanthropy, eligible grants or contracted stewardship funding"],
    ],
    disclaimer:
      "Illustrative only. Suitability depends on eligibility, project circumstances and independent assessment. Ecological benefits alone are not evidence of repayment capacity.",
  },
};

export const projectStages = {
  heading: "Where is your project now?",
  intro:
    "Select the stage closest to your situation. The suggestions are a starting point for a conversation, not an assessment of your project.",
  resultsLabel: "Suggested next steps",
  earlyNote:
    "At the early stages, favour low-cost investigation and clear decision points before acquisition or major expenditure.",
  stages: [
    {
      id: "exploring",
      label: "Exploring",
      summary: "A place or idea has been identified.",
      questions: [
        "What is the place, and who is already connected to it?",
        "What need or opportunity would the project respond to?",
        "Who should be involved from the start?",
      ],
      documents: [
        "A short project brief of one or two pages",
        "Notes on the place, its condition and existing rights",
        "A simple map of the people and organisations involved",
      ],
      funding: [
        "Small grants for community engagement or early research",
        "Philanthropic support for exploration",
        "In-kind and volunteer contributions",
      ],
      button: "Discuss an early idea",
    },
    {
      id: "organising",
      label: "Organising",
      summary: "People, purpose and responsibilities are taking shape.",
      questions: [
        "Who makes decisions, and how?",
        "What is the project's long-term purpose, and how is it protected?",
        "What roles, time and resources can each person realistically offer?",
      ],
      documents: [
        "A draft governance outline",
        "A statement of purpose and intended benefits",
        "A register of skills, time and resources available",
      ],
      funding: [
        "Grants for organisational development",
        "Philanthropic support for community-led design",
        "Pro bono or reduced-fee professional advice",
      ],
      button: "Discuss getting organised",
    },
    {
      id: "testing-feasibility",
      label: "Testing feasibility",
      summary: "Demand, permissions, costs and risks are being assessed.",
      questions: [
        "Is there real demand for what the project would produce or provide?",
        "What planning requirements, permissions or approvals apply?",
        "What are the honest costs, risks and realistic income?",
      ],
      documents: [
        "A feasibility summary with assumptions made explicit",
        "A preliminary budget and cash-flow outline",
        "Specialist assessments where needed (planning, ecological, financial)",
      ],
      funding: [
        "Grants or philanthropic support for feasibility work",
        "Contributions toward specialist advice",
        "Low-cost trials before major expenditure",
      ],
      button: "Discuss feasibility",
    },
    {
      id: "preparing-for-funding",
      label: "Preparing for funding",
      summary: "A defined proposal, budget and delivery plan are being developed.",
      questions: [
        "Which costs suit grants or philanthropy, and which could carry repayable finance?",
        "Who receives the funding and accounts for it?",
        "What terms, conditions and reporting can the project genuinely meet?",
      ],
      documents: [
        "A defined proposal with budget and delivery plan",
        "A funding plan matching sources to components",
        "Evidence of community support and governance arrangements",
      ],
      funding: [
        "Programme-matched grant applications",
        "Philanthropic partnerships",
        "Appropriately structured patient finance for income-producing components",
      ],
      button: "Discuss funding preparation",
    },
    {
      id: "delivering-and-sustaining",
      label: "Delivering and sustaining",
      summary: "Funding, implementation, maintenance and reporting need coordination.",
      questions: [
        "How are funds, milestones and reporting coordinated?",
        "Who pays for maintenance, replacement and long-term stewardship?",
        "What evidence would tell us to continue, adapt or stop?",
      ],
      documents: [
        "An implementation plan with clear decision points",
        "A maintenance and stewardship budget",
        "Reporting templates agreed with funders",
      ],
      funding: [
        "Staged grant or philanthropic agreements",
        "Repayable finance matched to proven income",
        "Contracted stewardship or service arrangements",
      ],
      button: "Discuss delivery support",
    },
  ],
};

export const funderPathway = {
  heading: "What would you like your support to make possible?",
  interestsLabel: "Areas of interest",
  interests: [
    "Secure community access to land",
    "Farmer and local enterprise livelihoods",
    "Shared productive infrastructure",
    "Restoration and long-term stewardship",
    "Early research and project development",
    "Skills, participation and community capability",
  ],
  contributionsLabel: "How you might contribute",
  contributions: [
    "Philanthropic support",
    "Grant funding",
    "Exploring patient investment or lending",
    "Land, facilities or equipment",
    "Professional expertise or introductions",
  ],
  body: "We welcome conversations with people and organisations interested in developing credible projects with lasting community and ecological benefit.",
  button: "Start a funding partnership conversation",
  note: "This site does not list a project marketplace, take donations, or offer vetted investment opportunities.",
};

export const riskSection = {
  heading: "Good funding starts with clear responsibilities.",
  introBefore:
    "Funding decisions need clarity about tenure, governance, repayment, maintenance and delivery responsibilities. We help projects identify the ",
  introLink: "questions that need to be resolved",
  introAfter: ", including where specialist advice is required.",
  introHref: "/approach#early-questions",
  closing:
    "We assess funding arrangements for their effect on local decision-making, long-term access to assets and the distribution of benefits and risks. Repayment terms, investor rights and exit arrangements should support the agreed purpose of the project and protect its capacity to endure.",
};

export const ourRole = {
  text: "From an initial idea to a proposal people can assess - ",
  linkText: "see what a funding-readiness engagement can produce",
  href: "/funding/projects",
};

export const fundingFooterNote =
  "Funding availability and suitability depend on project circumstances, eligibility and agreed terms.";

// --- Funding support introduction (website enhancement brief) -----------------

export const fundingSupport = {
  heading: "Funding Projects That Matter",
  body: [
    "Worthwhile projects can struggle to secure resources when their plans, responsibilities and operating needs are unclear. We help organisations identify suitable funding pathways and prepare proposals that connect local priorities with funder requirements.",
  ],
};

export const readinessLink = {
  text: "Preparing a project for funding conversations?",
  linkText: "See how a scoped funding-readiness engagement works",
  href: "/funding/projects",
};

export const beyondProjects = {
  heading: "Thinking Beyond Individual Projects",
  dsLink: {
    before: "Related projects may share infrastructure, partners or markets. ",
    linkText: "Bioregional Design Studios",
    after: " can help explore these connections before substantial funding is sought.",
    href: "/approach#design-studios",
  },
  closing:
    "Some activities generate trading income; others create the conditions that allow those enterprises to succeed. We explore coordinated funding for connected activities, including shared infrastructure, training, ecological restoration and market development, with each component supported by an appropriate source of finance.",
};

// --- Form option sets -------------------------------------------------------

export const projectStageOptions = projectStages.stages.map((s) => ({
  value: s.id,
  label: s.label,
}));

export const mainActivityOptions = [
  "Community land",
  "Community farm or local enterprise",
  "Restoration and stewardship",
  "A combination, or something else",
];

export const landPositionOptions = [
  { value: "exploring", label: "Exploring" },
  { value: "negotiating", label: "Negotiating" },
  { value: "leased", label: "Leased" },
  { value: "owned", label: "Owned" },
  { value: "other", label: "Other" },
];

export const preferredNextStepOptions = [
  "An introductory conversation",
  "Exchanging details by email",
  "Hearing about future opportunities",
];
